import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { api, setCsrfToken } from "../../lib/api.js";
import { useAuth } from "../../lib/auth-context.js";
import { AuthCardShell } from "../../components/auth-card-shell.js";

const Search = z.object({ token: z.string().optional() });

export const Route = createFileRoute("/_public/verify")({
  validateSearch: Search,
  component: VerifyPage,
});

type Status = "verifying" | "success" | "error" | "missing";

function VerifyPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState<Status>(token ? "verifying" : "missing");

  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const res = await api.post<{ ok: boolean; csrfToken?: string }>(
          "/auth/verify-email",
          { token },
        );
        if (res.data.ok && res.data.csrfToken) {
          setCsrfToken(res.data.csrfToken);
          await refresh();
          setStatus("success");
          // Brief pause so the user sees the success state.
          setTimeout(() => navigate({ to: "/onboarding" }), 600);
        } else {
          setStatus("error");
        }
      } catch {
        setStatus("error");
      }
    })();
  }, [token, navigate, refresh]);

  return (
    <AuthCardShell>
      <div className="brut-card">
        <h1 style={{ fontSize: 32, fontWeight: 900, letterSpacing: "-0.03em", margin: "0 0 12px" }}>
          Verify email
        </h1>
        {status === "missing" && (
          <p>
            No verification token in the URL. Check the link in your email, or{" "}
            <Link to="/login">return to sign in</Link>.
          </p>
        )}
        {status === "verifying" && <p>Verifying…</p>}
        {status === "success" && <p>Verified. Loading your workspace…</p>}
        {status === "error" && (
          <p>
            That link is invalid or expired.{" "}
            <Link to="/signup">Sign up again</Link> to get a fresh one.
          </p>
        )}
      </div>
    </AuthCardShell>
  );
}
